import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Moon, Bell, Clock, Sunrise } from 'lucide-react';
import { notificationApi } from '../services/api';
import type { NotificationSnoozeResponse } from '../types';
import { extractApiError } from '../utils/errorUtils';

interface NotificationSnoozeMenuProps {
  isOpen: boolean;
  onClose: (reason?: 'backdrop' | 'action' | 'escape') => void;
  anchorEl?: HTMLElement | null;
  menuRef?: React.RefObject<HTMLDivElement>;
}

/**
 * NotificationSnoozeMenu Component (Issue #823, #1109)
 *
 * Dropdown menu for snoozing notifications with:
 * - Quick duration options (30 min, 1 hr, 2 hr, 4 hr, Until tomorrow)
 * - "Resume notifications" button (if snoozed)
 * - Visual indicator showing snooze status
 * - Countdown timer showing time remaining
 * - Full keyboard navigation and ARIA support
 */
export const NotificationSnoozeMenu: React.FC<NotificationSnoozeMenuProps> = ({
  isOpen,
  onClose,
  anchorEl,
  menuRef
}) => {
  const [snoozeStatus, setSnoozeStatus] = useState<NotificationSnoozeResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<string>('');
  const [menuPosition, setMenuPosition] = useState<{ top: string; right: string }>({ top: '50px', right: '20px' });
  const firstFocusableRef = useRef<HTMLButtonElement>(null);
  const lastFocusableRef = useRef<HTMLButtonElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);

  const loadSnoozeStatus = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const preferences = await notificationApi.getPreferences();
      setSnoozeStatus({
        snooze_until: preferences.snooze_until,
        snooze_active: preferences.snooze_until ? new Date(preferences.snooze_until) > new Date() : false
      });
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      setError(apiError.message);
      console.error('Failed to load snooze status:', apiError);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Calculate menu position based on anchor element
  const calculatePosition = useCallback(() => {
    if (!anchorEl || typeof window === 'undefined') {
      setMenuPosition({ top: '50px', right: '20px' });
      return;
    }

    const rect = anchorEl.getBoundingClientRect();
    setMenuPosition({
      top: `${rect.bottom + 8}px`,
      right: `${window.innerWidth - rect.right}px`
    });
  }, [anchorEl]);

  // Load initial snooze status
  useEffect(() => {
    if (isOpen) {
      loadSnoozeStatus();
    }
  }, [isOpen, loadSnoozeStatus]);

  // Update position on open, scroll, and resize for dynamic positioning
  useEffect(() => {
    if (!isOpen) return;

    // Calculate initial position
    calculatePosition();

    // Recalculate on scroll and resize
    const handleUpdate = () => calculatePosition();
    window.addEventListener('scroll', handleUpdate, true); // Use capture to catch all scrolls
    window.addEventListener('resize', handleUpdate);

    return () => {
      window.removeEventListener('scroll', handleUpdate, true);
      window.removeEventListener('resize', handleUpdate);
    };
  }, [isOpen, calculatePosition]);

  // Update countdown timer every minute (only when menu is open)
  useEffect(() => {
    if (!isOpen || !snoozeStatus?.snooze_active || !snoozeStatus.snooze_until) {
      setTimeRemaining('');
      return;
    }

    const updateTimer = () => {
      const now = new Date();
      const snoozeEnd = new Date(snoozeStatus.snooze_until!);
      const diff = snoozeEnd.getTime() - now.getTime();

      if (diff <= 0) {
        // Clear local state immediately on expiry to avoid stuck UI during API failures
        setSnoozeStatus({ snooze_until: null, snooze_active: false });
        setTimeRemaining('');
        // Attempt to sync with backend, but don't block UI on failure
        loadSnoozeStatus().catch(err => {
          console.error('Failed to refresh snooze status after expiry:', err);
        });
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      if (hours > 0) {
        setTimeRemaining(`${hours}h ${minutes}m`);
      } else {
        setTimeRemaining(`${minutes}m`);
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [isOpen, snoozeStatus, loadSnoozeStatus]);

  // Focus management and keyboard navigation (WCAG 2.1 AA)
  useEffect(() => {
    if (isOpen) {
      // Store the currently focused element
      previousActiveElement.current = document.activeElement as HTMLElement;

      // Set initial focus to first focusable element
      setTimeout(() => {
        firstFocusableRef.current?.focus();
      }, 0);
    } else {
      // Return focus to trigger element when menu closes
      if (previousActiveElement.current && anchorEl) {
        anchorEl.focus();
      }
    }
  }, [isOpen, anchorEl]);

  // Escape key handler
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose('escape');
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Focus trap
  useEffect(() => {
    if (!isOpen) return;

    const handleTab = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;

      const firstFocusable = firstFocusableRef.current;
      const lastFocusable = lastFocusableRef.current;

      if (!firstFocusable || !lastFocusable) return;

      // Shift + Tab on first element: wrap to last
      if (event.shiftKey && document.activeElement === firstFocusable) {
        event.preventDefault();
        lastFocusable.focus();
      }
      // Tab on last element: wrap to first
      else if (!event.shiftKey && document.activeElement === lastFocusable) {
        event.preventDefault();
        firstFocusable.focus();
      }
    };

    document.addEventListener('keydown', handleTab);
    return () => document.removeEventListener('keydown', handleTab);
  }, [isOpen]);

  const handleSnooze = async (durationMinutes?: number, untilTomorrow?: boolean) => {
    try {
      setIsLoading(true);
      setError(null);

      let response: NotificationSnoozeResponse;

      if (untilTomorrow) {
        // Snooze until 8 AM tomorrow
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(8, 0, 0, 0);
        response = await notificationApi.snoozeNotifications({
          until_timestamp: tomorrow.toISOString()
        });
      } else if (durationMinutes) {
        response = await notificationApi.snoozeNotifications({ duration_minutes: durationMinutes });
      } else {
        throw new Error('Invalid snooze parameters');
      }

      setSnoozeStatus(response);
      onClose('action');
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      setError(apiError.message);
      console.error('Failed to snooze notifications:', apiError);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResume = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await notificationApi.resumeNotifications();
      setSnoozeStatus(response);
      onClose('action');
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      setError(apiError.message);
      console.error('Failed to resume notifications:', apiError);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40"
        onClick={() => onClose('backdrop')}
        aria-hidden="true"
        data-testid="snooze-menu-backdrop"
      />

      {/* Menu */}
      <div
        ref={menuRef}
        className="fixed z-50 w-80 bg-white rounded-lg shadow-lg border border-gray-200"
        style={{
          top: menuPosition.top,
          right: menuPosition.right
        }}
        role="dialog"
        aria-modal="true"
        aria-label="Notification snooze menu"
        data-testid="notification-snooze-menu"
      >
        {/* Header */}
        <div className="px-4 py-3 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Moon className="w-5 h-5" />
            Snooze Notifications
          </h3>
          {snoozeStatus?.snooze_active && timeRemaining && (
            <p className="text-sm text-gray-600 mt-1 flex items-center gap-1" aria-live="polite">
              <Clock className="w-4 h-4" />
              Snoozed for {timeRemaining}
            </p>
          )}
        </div>

        {/* Error message */}
        {error && (
          <div
            className="mx-4 mt-3 p-3 bg-red-50 border border-red-200 rounded-md"
            role="alert"
          >
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Menu options */}
        <div className="p-2">
          {snoozeStatus?.snooze_active ? (
            // Resume button when snoozed (both first and last focusable)
            <button
              ref={(el) => {
                if (el) {
                  (firstFocusableRef as React.MutableRefObject<HTMLButtonElement | null>).current = el;
                  (lastFocusableRef as React.MutableRefObject<HTMLButtonElement | null>).current = el;
                }
              }}
              onClick={handleResume}
              disabled={isLoading}
              className="w-full px-4 py-3 text-left hover:bg-gray-50 rounded-md transition-colors flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="resume-notifications-button"
              aria-label="Resume notifications"
            >
              <Bell className="w-5 h-5 text-secondary" />
              <div className="flex-1">
                <p className="font-medium text-gray-900">Resume Notifications</p>
                <p className="text-sm text-gray-500">Turn snooze off</p>
              </div>
            </button>
          ) : (
            // Snooze duration options
            <>
              <button
                ref={firstFocusableRef}
                onClick={() => handleSnooze(30)}
                disabled={isLoading}
                className="w-full px-4 py-3 text-left hover:bg-gray-50 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="snooze-30min-button"
                aria-label="Snooze for 30 minutes"
              >
                <p className="font-medium text-gray-900">30 minutes</p>
              </button>

              <button
                onClick={() => handleSnooze(60)}
                disabled={isLoading}
                className="w-full px-4 py-3 text-left hover:bg-gray-50 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="snooze-1hr-button"
                aria-label="Snooze for 1 hour"
              >
                <p className="font-medium text-gray-900">1 hour</p>
              </button>

              <button
                onClick={() => handleSnooze(120)}
                disabled={isLoading}
                className="w-full px-4 py-3 text-left hover:bg-gray-50 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="snooze-2hr-button"
                aria-label="Snooze for 2 hours"
              >
                <p className="font-medium text-gray-900">2 hours</p>
              </button>

              <button
                onClick={() => handleSnooze(240)}
                disabled={isLoading}
                className="w-full px-4 py-3 text-left hover:bg-gray-50 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="snooze-4hr-button"
                aria-label="Snooze for 4 hours"
              >
                <p className="font-medium text-gray-900">4 hours</p>
              </button>

              <button
                ref={lastFocusableRef}
                onClick={() => handleSnooze(undefined, true)}
                disabled={isLoading}
                className="w-full px-4 py-3 text-left hover:bg-gray-50 rounded-md transition-colors flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="snooze-tomorrow-button"
                aria-label="Snooze until tomorrow at 8 AM"
              >
                <Sunrise className="w-5 h-5 text-amber-600" />
                <div className="flex-1">
                  <p className="font-medium text-gray-900">Until tomorrow</p>
                  <p className="text-sm text-gray-500">Resume at 8:00 AM</p>
                </div>
              </button>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 rounded-b-lg">
          <p className="text-xs text-gray-600">
            Snoozed notifications won't appear until the snooze period ends.
            You can resume notifications anytime.
          </p>
        </div>
      </div>
    </>
  );
};

export default NotificationSnoozeMenu;
